import {NotificationService} from './notification-service';
import {Observable, forkJoin, of, merge} from 'rxjs';
import {NotificationItem} from '../models/notification-item';
import {pdsUtils} from '../pds-utils';
import { map } from 'rxjs/operators';

// this class will combine multiple notification service streams into a single stream of notification items
export class AggregatedNotificationService {
  notificationServices: Array<NotificationService>;

  //This this fire multiple times.
  //It will fire each time any of the services recognizes a new notification has come in.
  notificationStream: Observable<NotificationItem>;

  notificationServiceTypeMap: {[serviceType: string]: NotificationService} = {};

  constructor(notificationServices: Array<NotificationService>) {
    this.notificationServices = notificationServices;
    for (let service of this.notificationServices) {
      this.notificationServiceTypeMap[service.type] = service;
    }
  }

  /**
   * @returns {Observable} This observable will fire exactly once.
   */
  getExistingNotifications(): Observable<Array<NotificationItem>> {
    let serviceObservables: Array<Observable<Array<NotificationItem>>> = [];
    for (let notificationService of this.notificationServices) {
      serviceObservables.push(notificationService.getRecentNotifications(200));
    }
    return forkJoin(...serviceObservables).pipe(
      map((items: Array<Array<NotificationItem>>) => {
        return Array.prototype.concat(...items);
      })
    );
  }

  /**
   * The observable returned here fires exactly once to indicate the stream has been set up.
   * Once the stream has been set up you can subscribe to the notificationStream.
   * @returns {Observable} This observable will fire exactly once.
   */
  initializeNotificationStream(): Observable<void> {
    let streamObservables: Array<Observable<any>> = [];
    for (let notificationService of this.notificationServices) {
      streamObservables.push(notificationService.initializeNotificationStream());
    }
    return forkJoin(...streamObservables).pipe(
      map(() => {
        let notificationStreams: Array<Observable<NotificationItem>> = [];
        for (let notificationService of this.notificationServices) {
          let obs: Observable<NotificationItem> = notificationService.notificationStream;
          notificationStreams.push(obs);
        }
        this.notificationStream = merge(...notificationStreams); // merge combines multiple streams into one
      })
    );
  }

  markAsSeen(items: Array<NotificationItem>): Observable<any> {
    let groups = pdsUtils.groupBy(items, (item) => item.type);
    let observables = [of(undefined)];
    for (let groupName of Object.keys(groups)) {
      let service = this.notificationServiceTypeMap[groupName];
      if (service == null) {
        throw 'unmapped service (seen)';
      }
      observables.push(service.markAsSeen(groups[groupName]));
    }
    return forkJoin(observables).pipe(
      map(() => {
        return;
      })
    );
  }

  markAsRead(items: Array<NotificationItem>): Observable<any> {
    let groups = pdsUtils.groupBy(items, (item) => item.subtype || item.type);
    let observables = [of(undefined)];
    for (let groupName of Object.keys(groups)) {
      let service = this.notificationServiceTypeMap[groupName];
      if (service == null) {
        throw 'unmapped service (read)';
      }
      observables.push(service.markAsRead(groups[groupName]));
    }
    return forkJoin(observables).pipe(
      map(() => {
        return;
      })
    );
  }
}
